import ApiClient from '../apiClient';

// Revshare Offer interfaces - based on RefShareModelOfferData from backend
export interface RevshareOffer {
  _id: string;
  of_user_id: number;
  of_user_name: string;
  name: string;
  avatar?: string;
  type: 'FREE' | 'FREE_TRIAL' | 'free' | 'free trial';
  status?: 'active' | 'deactivated' | 'moderation';
  created_at?: string;

  // of_data from backend
  of_data?: {
    subscribers?: number;
    overall_popularity_percent?: number;
    likes?: number;
    posts?: number;
    favorited?: number;
    balance?: number;
    blocked_countries?: string[];
    arpu?: number;
    conversion?: number;
    last_update?: string;
  };

  // Owner data
  owner:
    | string
    | {
        _id: string;
        name: string;
        avatar?: string;
        type: 'creator' | 'agency';
      };

  // Offer data
  offer?: {
    commission: number;
    prohibited_sources: string[];
    show_service_info_for_partner: boolean;
    custom_media_for_partner: boolean;
    exist_chatters: boolean;
    chat_processing?: 'all time' | 'custom work time' | 'AI' | 'not specified';
    free_trial_access: boolean;
    description?: string;
    contact_for_content?: string;
    status:
      | 'moderation'
      | 'refusal'
      | 'hidden'
      | 'active'
      | 'MODERATION'
      | 'REFUSAL'
      | 'HIDDEN'
      | 'ACTIVE';
  };

  // Track link
  track_link?: {
    _id: string;
    link: string;
    track_link_id: number;
  };

  // Statistics
  revshare_campaigns_count?: number;
  sfs_posts_level?: 'High' | 'Medium' | 'Low' | 'None';
  ref_share_active_by_source?: Record<string, number>;
  ref_share_active_total?: number;

  // Public content
  public_content?: string[];

  // Granted (deposit info)
  granted?: {
    need: number;
    dep?: number | null;
  };

  // Deprecated - keeping for backward compatibility
  subscribers?: number;
  overall_popularity_percent?: number;
  likes?: number;
  posts?: number;
  favorited?: number;
}

// Sort options from backend RefShareOffersSort enum
export type RevshareOffersSortBy =
  | 'created_at'
  | '-created_at'
  | 'offer.commission'
  | '-offer.commission'
  | 'of_data.subscribers'
  | '-of_data.subscribers'
  | 'of_data.likes'
  | '-of_data.likes'
  | 'of_data.overall_popularity_percent'
  | '-of_data.overall_popularity_percent';

export interface RevshareOffersRequestParams {
  page?: number;
  page_size?: number;
  sort_by?: RevshareOffersSortBy;
  search?: string;
  type?: 'free' | 'free trial';
  performer_top?: number;
  commission?: number;
  guaranteed?: boolean;
  available_source_in?: string;
  available_source_out?: string;
  owner?: string;
  owner_type?: 'creator' | 'agency';
  model_id?: string;
}

export interface RevshareOffersApiResponse {
  page: number;
  page_size: number;
  count: number;
  objects: RevshareOffer[];
}

// Revshare Order interfaces
export interface RevshareOrderModel {
  id: string;
  of_user_name: string;
  of_user_id: number;
  client: string;
  link: string;
  track_link_id: number;
  avatar?: string;
  name?: string;
  overall_popularity_percent?: number;
  blocked_countries?: string[];
  transitions_count?: number;
}

export interface RevshareOrderOffer {
  commission: number;
  prohibited_sources: string[];
  show_service_info_for_partner: boolean;
  custom_media_for_partner: boolean;
  exist_chatters: boolean;
  chat_processing?: string;
  free_trial_access: boolean;
  description?: string;
  contact_for_content?: string;
  status: string;
  client: {
    id: string;
    name: string;
    avatar?: string;
    ref_share_statistic?: any;
  };
}

export interface RevshareOrderTransactionsStatistic {
  1?: number;
  total?: number;
}

export interface RevshareOrder {
  _id: string;
  id?: string;
  model: RevshareOrderModel;
  owner: string;
  type: 'FREE' | 'FREE_TRIAL';
  source: string;
  status: 'in_progress' | 'in_moderation' | 'waiting' | 'canceled' | 'completed';
  subscribe_days?: number;
  created_at: string;
  offer: RevshareOrderOffer;
  subscribed_count?: number;
  transactions_statistic?: RevshareOrderTransactionsStatistic;
  access_work_media?: any;
}

export interface CreateRevshareOrderRequest {
  model_id: string;
  type: 'free' | 'free trial';
  source: string;
  subscribe_days?: number;
  expire_days?: number;
}

export interface RevshareOrdersRequestParams {
  page?: number;
  page_size?: number;
  sort_by?:
    | 'created_at'
    | '-created_at'
    | 'subs.with_gte_3_chat_messages_count'
    | '-subs.with_gte_3_chat_messages_count'
    | 'transactions_statistic.1'
    | '-transactions_statistic.1'
    | 'transactions_statistic.total'
    | '-transactions_statistic.total'
    | 'subscribed_count'
    | '-subscribed_count'
    | 'romi'
    | '-romi'
    | 'arpu'
    | '-arpu';
  id?: string;
  source?: string;
  status?: 'in_progress' | 'in_moderation' | 'waiting' | 'canceled' | 'completed';
  model_type?: 'free' | 'free trial';
  models?: string[];
  subs_from?: number;
  income?: number;
}

export interface RevshareOrdersApiResponse {
  page: number;
  page_size: number;
  count: number;
  objects: RevshareOrder[];
  total_amount?: string;
}

// Cost interfaces
export interface RevshareCostModelData {
  _id: string;
  name: string;
  avatar?: string;
  of_user_name: string;
  overall_popularity_percent?: number;
}

export interface RevshareCostUserData {
  _id: string;
  name: string;
  avatar?: string;
}

export interface RevshareCostResponse {
  _id: string;
  amount: string;
  company_id: number;
  inner_company_id: string;
  status: 'in_pending' | 'paid';
  created_at: string;
  description: string | null;
  client: RevshareCostUserData;
  partner: RevshareCostUserData;
  model: RevshareCostModelData;
}

export interface RevshareCostsApiResponse {
  page: number;
  page_size: number;
  count: number;
  objects: RevshareCostResponse[];
  total_amount?: string;
}

// Companies Models interfaces
export interface CompaniesModelsRequestParams {
  page?: number;
  page_size?: number;
  of_user_name?: string;
  name?: string;
  id?: string;
  of_id?: number;
  owner_type?: 'creator' | 'agency';
  search?: string;
  status?: 'verified' | 'wait_media' | 'moderation' | 'active' | 'deactivated';
  company_type?: 'cpl' | 'refshare';
}

export interface CompaniesModelData {
  _id: string;
  of_user_id: number;
  of_user_name: string;
  name: string;
  avatar?: string;
  owner: string;
  status: 'verified' | 'wait_media' | 'moderation' | 'active' | 'deactivated';
  created_at?: string;
  type?: 'free' | 'free trial' | 'FREE' | 'FREE_TRIAL';
  overall_popularity?: number | null;
  public_content?: string[];
  work_content?: {
    nonude?: string;
    tt?: string;
    lingerie?: string;
    lite_nude?: string;
    full_nude?: string;
  };
  of_data?: {
    email?: string;
    subscribers?: number;
    overall_popularity_percent?: number;
    updated_at?: string;
    balance?: string;
    favorited?: number;
    posts?: number;
    blocked_countries?: string[];
  };
  granted?: {
    need?: number;
    dep?: number | null;
    transactions?: any;
    updated_at?: string | null;
  };
  company_types?: ('cpl' | 'refshare')[];
  companies_count?: number;
}

export interface CompaniesModelsApiResponse {
  page: number;
  page_size: number;
  count: number;
  objects: CompaniesModelData[];
}

const partnerRevshareService = {
  // Get list of models with companies (for revshare accounts page)
  getCompaniesModels: (
    params: CompaniesModelsRequestParams = {}
  ): Promise<CompaniesModelsApiResponse> => {
    const searchParams = new URLSearchParams();
    searchParams.append('page', (params.page || 1).toString());
    searchParams.append('page_size', (params.page_size || 10).toString());

    if (params.of_user_name) searchParams.append('of_user_name', params.of_user_name);
    if (params.name) searchParams.append('name', params.name);
    if (params.id) searchParams.append('id', params.id);
    if (params.of_id !== undefined) searchParams.append('of_id', params.of_id.toString());
    if (params.owner_type) searchParams.append('owner_type', params.owner_type);
    if (params.search) searchParams.append('search', params.search);
    if (params.status) searchParams.append('status', params.status);
    if (params.company_type) searchParams.append('company_type', params.company_type);

    return ApiClient.get(`/partners/models/companies?${searchParams.toString()}`).then(
      (response) => response.data
    );
  },

  // Get list of available revshare offers
  getOffers: (params: RevshareOffersRequestParams = {}): Promise<RevshareOffersApiResponse> => {
    const searchParams = new URLSearchParams();

    searchParams.append('page', (params.page || 1).toString());
    searchParams.append('page_size', (params.page_size || 10).toString());
    searchParams.append('sort_by', params.sort_by || 'created_at');
    if (params.search) searchParams.append('search', params.search);
    if (params.type) searchParams.append('type', params.type);
    if (params.performer_top !== undefined)
      searchParams.append('performer_top', params.performer_top.toString());
    if (params.commission !== undefined)
      searchParams.append('commission', params.commission.toString());
    if (params.guaranteed !== undefined)
      searchParams.append('guaranteed', params.guaranteed.toString());
    if (params.available_source_in)
      searchParams.append('available_source_in', params.available_source_in);
    if (params.available_source_out)
      searchParams.append('available_source_out', params.available_source_out);
    if (params.owner) searchParams.append('owner', params.owner);
    if (params.owner_type) searchParams.append('owner_type', params.owner_type);
    if (params.model_id) searchParams.append('model_id', params.model_id);

    return ApiClient.get(`/partners/ref_share/offers?${searchParams.toString()}`).then(
      (response) => response.data
    );
  },

  // Create a new revshare order (request tracking link)
  createOrder: (data: CreateRevshareOrderRequest) => {
    return ApiClient.post(`/partners/ref_share/orders`, data);
  },

  // Cancel/stop a revshare order (campaign)
  cancelOrder: (orderId: string): Promise<void> => {
    return ApiClient.delete(`/partners/ref_share/orders/${orderId}`);
  },

  // Update a revshare order (campaign) - name and description
  updateOrder: (orderId: string, data: { name?: string; description?: string }): Promise<any> => {
    return ApiClient.patch(`/partners/ref_share/orders/${orderId}`, data).then(
      (response) => response.data
    );
  },

  // Get list of partner's revshare orders (campaigns)
  getOrders: (params: RevshareOrdersRequestParams = {}): Promise<RevshareOrdersApiResponse> => {
    const searchParams = new URLSearchParams();
    searchParams.append('page', (params.page || 1).toString());
    searchParams.append('page_size', (params.page_size || 10).toString());

    if (params.sort_by) searchParams.append('sort_by', params.sort_by);
    if (params.id) searchParams.append('search', params.id);
    if (params.source) searchParams.append('source', params.source);
    if (params.status) searchParams.append('status', params.status);
    if (params.model_type) searchParams.append('model_type', params.model_type);
    if (params.models && params.models.length > 0) {
      params.models.forEach((modelId) => searchParams.append('models', modelId));
    }
    if (params.subs_from !== undefined)
      searchParams.append('subs_from', params.subs_from.toString());
    if (params.income !== undefined) searchParams.append('income', params.income.toString());

    return ApiClient.get(`/partners/ref_share/orders?${searchParams.toString()}`).then(
      (response) => response.data
    );
  },

  // Get list of partner's revshare income
  getIncome: (
    params: {
      page?: number;
      page_size?: number;
      account?: string;
      campaign?: string;
      period?: string;
      status?: string;
      date_period?: string;
    } = {}
  ): Promise<any> => {
    const searchParams = new URLSearchParams();
    searchParams.append('page', (params.page || 1).toString());
    searchParams.append('page_size', (params.page_size || 10).toString());

    if (params.account && params.account !== 'all') searchParams.append('account', params.account);
    if (params.campaign && params.campaign !== 'all')
      searchParams.append('campaign', params.campaign);
    if (params.period && params.period !== 'all') searchParams.append('period', params.period);
    if (params.status && params.status !== 'all') searchParams.append('status', params.status);
    if (params.date_period && params.date_period !== 'all')
      searchParams.append('date_period', params.date_period);

    return ApiClient.get(`/partners/ref_share/income?${searchParams.toString()}`).then(
      (response) => response.data
    );
  },

  // Get income statistics
  getIncomeStats: (params?: {
    account?: string;
    campaign?: string;
    period?: string;
    date_period?: string;
  }): Promise<any> => {
    const searchParams = new URLSearchParams();

    if (params?.account && params.account !== 'all') searchParams.append('account', params.account);
    if (params?.campaign && params.campaign !== 'all')
      searchParams.append('campaign', params.campaign);
    if (params?.period && params.period !== 'all') searchParams.append('period', params.period);
    if (params?.date_period && params.date_period !== 'all')
      searchParams.append('date_period', params.date_period);

    const queryString = searchParams.toString();
    const url = queryString
      ? `/partners/ref_share/income/stats?${queryString}`
      : '/partners/ref_share/income/stats';
    return ApiClient.get(url).then((response) => response.data);
  },

  // Get list of partner's costs
  getCosts: (
    params: {
      page?: number;
      page_size?: number;
      status?: 'in_pending' | 'paid';
      search?: string;
      company_id?: number;
      inner_company_id?: string;
      amount?: number;
      created_at__start?: string;
      created_at__end?: string;
      model_id?: string;
    } = {}
  ): Promise<RevshareCostsApiResponse> => {
    const searchParams = new URLSearchParams();
    searchParams.append('page', (params.page || 1).toString());
    searchParams.append('page_size', (params.page_size || 10).toString());

    if (params.status) searchParams.append('status', params.status);
    if (params.search) searchParams.append('search', params.search);
    if (params.company_id) searchParams.append('company_id', params.company_id.toString());
    if (params.inner_company_id) searchParams.append('inner_company_id', params.inner_company_id);
    if (params.amount !== undefined) searchParams.append('amount', params.amount.toString());
    if (params.created_at__start)
      searchParams.append('created_at__start', params.created_at__start);
    if (params.created_at__end) searchParams.append('created_at__end', params.created_at__end);
    if (params.model_id) searchParams.append('model_id', params.model_id);

    return ApiClient.get(`/partners/ref_share/costs?${searchParams.toString()}`).then(
      (response) => response.data
    );
  },

  // Create a new cost/payment request
  createCost: (data: {
    amount: number;
    client_id?: string;
    company_id: number;
    description: string;
    inner_company_id: string;
  }): Promise<any> => {
    return ApiClient.post('/partners/ref_share/costs', data).then((response) => response.data);
  },

  // Get list of partner's subscribers
  getSubscribers: (
    params: RevshareSubscribersRequestParams = {}
  ): Promise<RevshareSubscribersApiResponse> => {
    const searchParams = new URLSearchParams();
    searchParams.append('page', (params.page || 1).toString());
    searchParams.append('page_size', (params.page_size || 10).toString());

    if (params.sort_by) searchParams.append('sort_by', params.sort_by);
    if (params.id) searchParams.append('id', params.id);
    if (params.name) searchParams.append('search', params.name);
    if (params.of_company_id !== undefined)
      searchParams.append('of_company_id', params.of_company_id.toString());
    if (params.company_id) searchParams.append('company_id', params.company_id);
    if (params.model_id) searchParams.append('model_id', params.model_id);
    if (params.of_id !== undefined) searchParams.append('of_id', params.of_id.toString());
    if (params.of_model_id !== undefined)
      searchParams.append('of_model_id', params.of_model_id.toString());
    if (params.has_gte_three_messages !== undefined)
      searchParams.append('has_gte_three_messages', params.has_gte_three_messages.toString());
    if (params.one_transaction !== undefined)
      searchParams.append('one_transaction', params.one_transaction.toString());
    if (params.two_transaction !== undefined)
      searchParams.append('two_transaction', params.two_transaction.toString());
    if (params.five_transaction !== undefined)
      searchParams.append('five_transaction', params.five_transaction.toString());
    if (params.amount_more_500 !== undefined)
      searchParams.append('amount_more_500', params.amount_more_500.toString());
    if (params.compaign_type) searchParams.append('compaign_type', params.compaign_type);

    return ApiClient.get(`/partners/subscribers?${searchParams.toString()}`).then(
      (response) => response.data
    );
  },

  // Get list of partner's transactions
  getTransactions: (
    params: RevshareTransactionsRequestParams = {}
  ): Promise<RevshareTransactionsApiResponse> => {
    const searchParams = new URLSearchParams();
    searchParams.append('page', (params.page || 1).toString());
    searchParams.append('page_size', (params.page_size || 10).toString());

    if (params.id) searchParams.append('search', params.id);
    if (params.of_id !== undefined) searchParams.append('of_id', params.of_id.toString());
    if (params.of_company_id !== undefined)
      searchParams.append('of_company_id', params.of_company_id.toString());
    if (params.company_id) searchParams.append('company_id', params.company_id);
    if (params.model_id) searchParams.append('model_id', params.model_id);
    if (params.of_model_id !== undefined)
      searchParams.append('of_model_id', params.of_model_id.toString());
    if (params.type) searchParams.append('type', params.type);
    if (params.status) searchParams.append('status', params.status);
    if (params.company_status) searchParams.append('company_status', params.company_status);
    if (params.start_date) searchParams.append('start_date', params.start_date);
    if (params.end_date) searchParams.append('end_date', params.end_date);
    if (params.campaign_type) searchParams.append('compaign_type', params.campaign_type);

    return ApiClient.get(`/partners/transactions?${searchParams.toString()}`).then(
      (response) => response.data
    );
  },

  // Get total amounts for transactions
  getTransactionsAllAmount: (
    params: Omit<RevshareTransactionsRequestParams, 'page' | 'page_size'> = {}
  ): Promise<RevshareTransactionsAllAmountResponse> => {
    const searchParams = new URLSearchParams();

    if (params.id) searchParams.append('id', params.id);
    if (params.of_id !== undefined) searchParams.append('of_id', params.of_id.toString());
    if (params.of_company_id !== undefined)
      searchParams.append('of_company_id', params.of_company_id.toString());
    if (params.company_id) searchParams.append('company_id', params.company_id);
    if (params.model_id) searchParams.append('model_id', params.model_id);
    if (params.of_model_id !== undefined)
      searchParams.append('of_model_id', params.of_model_id.toString());
    if (params.type) searchParams.append('type', params.type);
    if (params.status) searchParams.append('status', params.status);
    if (params.company_status) searchParams.append('company_status', params.company_status);
    if (params.start_date) searchParams.append('start_date', params.start_date);
    if (params.end_date) searchParams.append('end_date', params.end_date);
    if (params.campaign_type) searchParams.append('compaign_type', params.campaign_type);

    const queryString = searchParams.toString();
    const url = queryString
      ? `/partners/transactionsall_amount?${queryString}`
      : '/partners/transactionsall_amount';
    return ApiClient.get(url).then((response) => response.data);
  },
};

// Subscriber interfaces - based on backend schemas
export interface SubscriberInnerCompany {
  id: string;
  _id?: string; // Keep for backwards compatibility
  created_at: string;
  source: string;
  of_id: number;
  link?: string;
  transitions_count: number;
  commition: number;
  type?: 'cpl' | 'refshare';
  subs?: {
    ge?: number;
    price: string | number;
    min_price?: number;
    max_price?: number;
  };
}

export interface SubscriberInnerModel {
  id: string;
  _id?: string; // Keep for backwards compatibility
  avatar: string;
  name: string;
  of_user_name: string;
}

export interface RevshareSubscriberData {
  id: string;
  _id?: string; // Keep for backwards compatibility
  created_at: string;
  name: string;
  username?: string;
  of_user_id?: number;
  of_model_id?: number;
  client?: string;
  more_3_chat_messages?: boolean | null;
  transactions_count?: number;
  transactions_total_amount?: string | number;
  company?: SubscriberInnerCompany;
  model?: SubscriberInnerModel;
}

export interface RevshareSubscribersRequestParams {
  page?: number;
  page_size?: number;
  sort_by?: 'created_at' | '-created_at';
  id?: string;
  name?: string;
  of_company_id?: number;
  company_id?: string;
  model_id?: string;
  of_id?: number;
  of_model_id?: number;
  has_gte_three_messages?: boolean;
  one_transaction?: boolean;
  two_transaction?: boolean;
  five_transaction?: boolean;
  amount_more_500?: boolean;
  compaign_type?: 'cpl' | 'refshare';
}

export interface RevshareSubscribersApiResponse {
  page: number;
  page_size: number;
  count: number;
  objects: RevshareSubscriberData[];
}

// Transaction interfaces - based on backend schemas
export interface TransactionInnerCompany {
  id: string;
  _id?: string; // backward compatibility
  created_at: string;
  source: string;
  of_id: number;
  link?: string;
  transitions_count: number;
  commition: number;
  type?: 'cpl' | 'refshare';
  subs?: {
    ge?: number;
    price: string;
    min_price?: number;
    max_price?: number;
  };
}

export interface TransactionInnerModel {
  id: string;
  _id?: string; // backward compatibility
  of_id: number;
  avatar: string;
  name: string;
  of_user_name: string;
}

export interface TransactionInnerSubscriber {
  id: string;
  _id?: string; // backward compatibility
  of_id: number;
  name: string;
  username: string;
}

export type TransactionType =
  | 'tips'
  | 'subscription'
  | 'message'
  | 'post'
  | 'stream'
  | 'chat_messages';

export interface RevshareTransactionData {
  id: string;
  _id?: string; // backward compatibility
  created_at: string;
  of_id: string;
  of_model_id: number;
  of_subscriber_id: number;
  fee: string;
  amount: string;
  transaction_type: TransactionType;
  owner: string;
  status?: string;
  company_status?: string;
  model?: TransactionInnerModel;
  company?: TransactionInnerCompany;
  subscriber?: TransactionInnerSubscriber;
  compaign_type?: 'cpl' | 'refshare';
}

export interface RevshareTransactionsRequestParams {
  page?: number;
  page_size?: number;
  id?: string;
  of_id?: number;
  of_company_id?: number;
  company_id?: string;
  model_id?: string;
  of_model_id?: number;
  type?: TransactionType;
  status?: string;
  company_status?: string;
  start_date?: string;
  end_date?: string;
  campaign_type?: 'cpl' | 'refshare';
}

export interface RevshareTransactionsApiResponse {
  page: number;
  page_size: number;
  count: number;
  objects: RevshareTransactionData[];
  total_amount?: string;
  total_fee?: string;
}

export interface RevshareTransactionsAllAmountResponse {
  total_amount: number;
  total_income: number;
  total_count: number;
}

// Daily Earns interfaces
export type CampaignType = 'cpl' | 'ref_share';
export type TrafficSource =
  | 'only_fans'
  | 'reddit'
  | 'instagram'
  | 'tiktok'
  | 'google'
  | 'telegram'
  | 'twitter'
  | 'youtube'
  | 'another';

export interface DailyEarnData {
  date: string;
  compaign_type: CampaignType;
  total_amount: number;
  total_subscribers: number;
}

export interface MonthlyEarnData {
  month: string;
  compaign_type: CampaignType;
  total_amount: number;
  total_subscribers: number;
}

export interface WeeklyEarnData {
  week: string;
  compaign_type: CampaignType;
  total_amount: number;
  total_subscribers: number;
}

export interface EarnStats {
  total_amount_all: number;
  total_amount_cpl: number;
  total_spending: number;
}

export interface EarnsRequestParams {
  page?: number;
  page_size?: number;
  model_id?: number;
  source?: TrafficSource;
  company_inner_id?: string;
  start_date?: string; // Format: YYYY-MM-DD
  end_date?: string; // Format: YYYY-MM-DD
}

export interface DailyEarnsApiResponse {
  page: number;
  page_size: number;
  count: number;
  objects: DailyEarnData[];
}

export interface MonthlyEarnsApiResponse {
  page: number;
  page_size: number;
  count: number;
  objects: MonthlyEarnData[];
}

export interface WeeklyEarnsApiResponse {
  page: number;
  page_size: number;
  count: number;
  objects: WeeklyEarnData[];
}

// Partner earns service methods
export const partnerEarnsService = {
  // Get daily earnings (grouped by day and campaign type)
  getDailyEarns: (params: EarnsRequestParams = {}): Promise<DailyEarnsApiResponse> => {
    const searchParams = new URLSearchParams();
    searchParams.append('page', (params.page || 1).toString());
    searchParams.append('page_size', (params.page_size || 10).toString());

    if (params.model_id !== undefined) searchParams.append('model_id', params.model_id.toString());
    if (params.source) searchParams.append('source', params.source);
    if (params.company_inner_id) searchParams.append('company_inner_id', params.company_inner_id);
    if (params.start_date) searchParams.append('start_date', params.start_date);
    if (params.end_date) searchParams.append('end_date', params.end_date);

    return ApiClient.get(`/partners/earns/by_days?${searchParams.toString()}`).then(
      (response) => response.data
    );
  },

  // Get weekly earnings (grouped by week and campaign type)
  getWeeklyEarns: (params: EarnsRequestParams = {}): Promise<WeeklyEarnsApiResponse> => {
    const searchParams = new URLSearchParams();
    searchParams.append('page', (params.page || 1).toString());
    searchParams.append('page_size', (params.page_size || 10).toString());

    if (params.model_id !== undefined) searchParams.append('model_id', params.model_id.toString());
    if (params.source) searchParams.append('source', params.source);
    if (params.company_inner_id) searchParams.append('company_inner_id', params.company_inner_id);
    if (params.start_date) searchParams.append('start_date', params.start_date);
    if (params.end_date) searchParams.append('end_date', params.end_date);

    return ApiClient.get(`/partners/earns/by_weeks?${searchParams.toString()}`).then(
      (response) => response.data
    );
  },

  // Get monthly earnings (grouped by month and campaign type)
  getMonthlyEarns: (params: EarnsRequestParams = {}): Promise<MonthlyEarnsApiResponse> => {
    const searchParams = new URLSearchParams();
    searchParams.append('page', (params.page || 1).toString());
    searchParams.append('page_size', (params.page_size || 10).toString());

    if (params.model_id !== undefined) searchParams.append('model_id', params.model_id.toString());
    if (params.source) searchParams.append('source', params.source);
    if (params.company_inner_id) searchParams.append('company_inner_id', params.company_inner_id);
    if (params.start_date) searchParams.append('start_date', params.start_date);
    if (params.end_date) searchParams.append('end_date', params.end_date);

    return ApiClient.get(`/partners/earns/by_months?${searchParams.toString()}`).then(
      (response) => response.data
    );
  },

  // Get earns statistics (total amounts)
  getEarnsStats: (): Promise<EarnStats> => {
    return ApiClient.get('/partners/earns/stats').then((response) => response.data);
  },
};

// SFS Activity Tags interfaces
export interface SfsTagData {
  tag_id: number;
  avatar: string;
  user_id: number;
  user_name: string;
  name: string;
  created_at: string;
}

export interface SfsIntoTagsResponse {
  list: SfsTagData[];
  hasMore: boolean;
  lastId?: number;
}

export interface SfsOutTagsResponse {
  items: SfsTagData[];
  hasMore: boolean;
}

export interface SfsIntoTagsRequestParams {
  of_model_id: number;
  from_id?: number;
  page_size?: number;
}

export interface SfsOutTagsRequestParams {
  of_model_id: number;
  page?: number;
  page_size?: number;
}

// SFS Activity service methods
export const partnerSfsActivityService = {
  // Get incoming tags (who tagged this model) - cursor-based pagination
  getIntoTags: (params: SfsIntoTagsRequestParams): Promise<SfsIntoTagsResponse> => {
    const searchParams = new URLSearchParams();
    searchParams.append('page_size', (params.page_size || 10).toString());
    if (params.from_id !== undefined) {
      searchParams.append('from_id', params.from_id.toString());
    }

    return ApiClient.get(`/partners/models/companies/${params.of_model_id}/into_tags?${searchParams.toString()}`).then(response => response.data);
  },

  // Get outgoing tags (who this model tagged) - page-based pagination
  getOutTags: (params: SfsOutTagsRequestParams): Promise<SfsOutTagsResponse> => {
    const searchParams = new URLSearchParams();
    searchParams.append('page', (params.page || 1).toString());
    searchParams.append('page_size', (params.page_size || 10).toString());

    return ApiClient.get(`/partners/models/companies/${params.of_model_id}/outo_tags?${searchParams.toString()}`).then(response => response.data);
  },
};

export default partnerRevshareService;
